import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const slug = (s) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "").slice(0, 40);

export const DepartmentsTab = ({ permLabel, onSaved }) => {
  const [data, setData] = useState(null);
  const [deps, setDeps] = useState([]);
  const [newLabel, setNewLabel] = useState("");
  const load = () => api.get("/departments").then((r) => { setData(r.data); setDeps(r.data.departments); }).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  if (!data) return null;
  const toggle = (i, p) => setDeps((ds) => ds.map((d, j) => (j === i ? { ...d, permissions: d.permissions.includes(p) ? d.permissions.filter((x) => x !== p) : [...d.permissions, p] } : d)));
  const add = () => { const k = slug(newLabel); if (k.length < 2) { toast.error("Scrivi un nome di almeno 2 lettere o cifre"); return; } if (deps.some((d) => d.key === k)) { toast.error("Reparto già presente"); return; } setDeps([...deps, { key: k, label: newLabel.trim(), permissions: [] }]); setNewLabel(""); };
  const save = async () => {
    try { const r = await api.put("/departments", deps.map(({ key, label, permissions }) => ({ key, label, permissions }))); setData(r.data); setDeps(r.data.departments); toast.success("Reparti salvati"); onSaved?.(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="departments-tab">
      <p className="mb-4 max-w-3xl text-sm text-muted-foreground">
        I reparti limitano responsabili e assistenti alle aree indicate: un utente con uno o più reparti vede solo i permessi del suo ruolo compresi nei suoi reparti, più ordini e anagrafiche in lettura. Direzione e amministratori vedono sempre tutto; gli utenti senza reparto mantengono tutti i permessi del ruolo.
      </p>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Input className="h-9 w-72 bg-white" placeholder="Nuovo reparto (es. Qualità e sicurezza)" value={newLabel} onChange={(e) => setNewLabel(e.target.value)} data-testid="department-new-label" />
        <Button size="sm" variant="outline" onClick={add} data-testid="department-add-button"><Plus className="mr-1 h-4 w-4" />Aggiungi reparto</Button>
        <Button size="sm" className="ml-auto" onClick={save} data-testid="departments-save-button">Salva reparti</Button>
      </div>
      <div className="overflow-x-auto border bg-white">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead className="sticky left-0 z-10 min-w-[220px] bg-muted">Permesso</TableHead>
              {deps.map((d, i) => (
                <TableHead key={d.key} className="min-w-[150px] align-top">
                  <Input className="h-8 bg-white text-xs font-semibold" value={d.label} onChange={(e) => setDeps(deps.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} data-testid={`department-label-${d.key}`} />
                  <button className="mt-1 flex items-center gap-1 text-[11px] text-destructive hover:underline" onClick={() => window.confirm(`Eliminare il reparto ${d.label}? Si può eliminare solo se non è assegnato a nessun utente.`) && setDeps(deps.filter((_, j) => j !== i))} data-testid={`department-delete-${d.key}`}><Trash2 className="h-3 w-3" />Elimina</button>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.assignable.map((p) => (
              <TableRow key={p}>
                <TableCell className="sticky left-0 z-10 bg-white text-sm">{permLabel[p] || p}<div className="font-mono text-[10px] text-muted-foreground">{p}{data.base.includes(p) && " · sempre incluso"}</div></TableCell>
                {deps.map((d, i) => (
                  <TableCell key={d.key} className="text-center">
                    <Checkbox checked={data.base.includes(p) || d.permissions.includes(p)} disabled={data.base.includes(p)} onCheckedChange={() => toggle(i, p)} data-testid={`department-perm-${d.key}-${p.replace(":", "-")}`} />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
